import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

import { describeError, logSessionEvent } from './session/session-log';
import { DEFAULT_EXPIRES_IN_S, ITokenSet } from './token-set';

const TOKENS_KEY = 'alberta.auth.tokens';

/** Storage key of the previous OAuth library (ionic-appauth), keychain on mobile, localStorage on the web. */
const LEGACY_APPAUTH_KEY = 'token_response';
/** localStorage keys of the previous web OAuth library (angular-oauth2-oidc). */
const LEGACY_WEB_KEYS = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  idToken: 'id_token',
  expiresAt: 'expires_at',
  leftovers: [
    'access_token_stored_at',
    'id_token_claims_obj',
    'id_token_expires_at',
    'id_token_stored_at',
    'granted_scopes',
    'nonce',
    'PKCE_verifier',
    'session_state',
  ],
};

/** What the storage answered when it was read. */
export type TokenState = 'token-present' | 'no-token' | 'unknown';

/** The one platform difference: where a string is kept. A missing key is `null`; only a failing storage rejects. */
interface IKeyValueBackend {
  read(key: string): Promise<string | null>;
  write(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

const keychain: IKeyValueBackend = {
  async read(key) {
    const keys = (await SecureStoragePlugin.keys()).value ?? [];
    return keys.includes(key) ? ((await SecureStoragePlugin.get({ key })).value ?? null) : null;
  },
  async write(key, value) {
    await SecureStoragePlugin.set({ key, value });
  },
  async remove(key) {
    await SecureStoragePlugin.remove({ key }).catch(() => undefined);
  },
};

const browserStorage: IKeyValueBackend = {
  async read(key) {
    return localStorage.getItem(key);
  },
  async write(key, value) {
    localStorage.setItem(key, value);
  },
  async remove(key) {
    localStorage.removeItem(key);
  },
};

/**
 * Persists the OAuth tokens: in the keychain / keystore on the devices, in localStorage on the web.
 *
 * Reads never throw: a token that cannot be read counts as "no token" and `lastReadState` tells
 * the caller whether the storage answered at all. The one-time migration of the previous
 * libraries' tokens keeps existing users signed in across the update; it can be removed one
 * release after the rollout.
 */
@Injectable({ providedIn: 'root' })
export class TokenStore {
  private readonly _backend: IKeyValueBackend;
  private readonly _isHybrid: boolean;
  private _lastReadState: TokenState = 'unknown';

  constructor(platform: Platform) {
    this._isHybrid = platform.is('hybrid');
    this._backend = this._isHybrid ? keychain : browserStorage;
  }

  /** `unknown` until the first `load()`, then whether the storage answered and what it held. */
  public get lastReadState(): TokenState {
    return this._lastReadState;
  }

  public async load(): Promise<ITokenSet | null> {
    try {
      const stored = await this._backend.read(TOKENS_KEY);
      const tokens = stored ? parseTokenSet(stored) : await this.migrateLegacyTokens();
      this._lastReadState = tokens ? 'token-present' : 'no-token';
      return tokens;
    } catch (error) {
      this._lastReadState = 'unknown';
      // a breadcrumb only: the session retries the read and reports when it gives up
      window.logger?.addBreadcrumb({
        category: 'auth',
        message: `[TokenStore] the token storage did not answer: ${describeError(error)}`,
      });
      return null;
    }
  }

  public async save(tokens: ITokenSet): Promise<void> {
    await this._backend.write(TOKENS_KEY, JSON.stringify(tokens));
    this._lastReadState = 'token-present';
  }

  public async clear(): Promise<void> {
    await this._backend.remove(TOKENS_KEY);
    // otherwise `tokenState` would still report the token of the ended session after a logout
    this._lastReadState = 'no-token';
  }

  private async migrateLegacyTokens(): Promise<ITokenSet | null> {
    const appAuthTokens = await this.readLegacyAppAuth();
    const tokens = appAuthTokens ?? (this._isHybrid ? null : readLegacyWeb());
    if (!tokens) {
      return null;
    }
    const source = appAuthTokens ? 'appauth' : 'oauth2-oidc';
    try {
      await this.save(tokens);
      await this._backend.remove(LEGACY_APPAUTH_KEY);
      if (!this._isHybrid) {
        const { leftovers, ...tokenKeys } = LEGACY_WEB_KEYS;
        [...Object.values(tokenKeys), ...leftovers].forEach(key => localStorage.removeItem(key));
      }
      logSessionEvent('token_migration', source, 'migrated the tokens of the previous OAuth library');
    } catch (error) {
      // the token is still good for this process; the legacy keys stay, so the next start migrates again
      logSessionEvent(
        'token_migration_failed',
        source,
        `the migrated token could not be saved: ${describeError(error)}`,
        error
      );
    }
    return tokens;
  }

  private async readLegacyAppAuth(): Promise<ITokenSet | null> {
    const raw = await this._backend.read(LEGACY_APPAUTH_KEY);
    if (!raw) {
      return null;
    }
    try {
      const legacy = JSON.parse(raw);
      if (!legacy?.access_token) {
        return null;
      }
      const expiresIn = Number(legacy.expires_in ?? DEFAULT_EXPIRES_IN_S);
      return {
        accessToken: legacy.access_token,
        refreshToken: legacy.refresh_token ?? null,
        idToken: legacy.id_token ?? null,
        expiresAt: (Number(legacy.issued_at) + expiresIn) * 1000,
        expiresIn,
      };
    } catch {
      return null;
    }
  }
}

function readLegacyWeb(): ITokenSet | null {
  const accessToken = localStorage.getItem(LEGACY_WEB_KEYS.accessToken);
  if (!accessToken) {
    return null;
  }
  const expiresAt = Number(localStorage.getItem(LEGACY_WEB_KEYS.expiresAt) ?? 0);
  return {
    accessToken,
    refreshToken: localStorage.getItem(LEGACY_WEB_KEYS.refreshToken),
    idToken: localStorage.getItem(LEGACY_WEB_KEYS.idToken),
    expiresAt,
    expiresIn: Math.max(0, Math.round((expiresAt - Date.now()) / 1000)),
  };
}

function parseTokenSet(raw: string): ITokenSet | null {
  try {
    const tokens = JSON.parse(raw) as ITokenSet;
    return tokens?.accessToken ? tokens : null;
  } catch {
    return null;
  }
}
